import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft, ScrollText, ShieldCheck, AlertCircle, Scale, Copyright, Ban } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT Logo" 
                  className="w-10 h-10 rounded-lg object-cover" 
                />
            </Link>
            <span className="text-xl font-bold tracking-tight">FaheemGPT <span className="text-slate-400 font-normal">Intelligent Tutoring Platform</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
                <Button variant="ghost" className="flex gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 py-16 px-6 bg-slate-50/50">
        <div className="max-w-4xl mx-auto">
            
            <div className="text-center mb-12 space-y-4">
                <div className="inline-flex items-center justify-center p-3 bg-blue-50 rounded-xl mb-2">
                    <Scale className="w-8 h-8 text-blue-600" />
                </div>
                <h1 className="text-4xl font-bold text-slate-900">Terms of Service</h1>
                <p className="text-slate-600 text-lg">Last Updated: October 26, 2025</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Introduction */}
                <div className="p-8 border-b border-slate-100">
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Welcome to FaheemGPT. By accessing or using our Intelligent Tutoring Platform, you agree to be bound by these Terms of Service. Please read them carefully as they govern your use of our educational tools and services.
                    </p>
                </div>

                <div className="divide-y divide-slate-100">
                    
                    {/* Section 1 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-green-100 rounded-lg">
                                <ShieldCheck className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">1. Acceptance of Terms</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    By creating an account or using FaheemGPT, you agree to these terms in full. If you do not agree with any part of these terms, you may not use our services. These terms apply to all visitors, users, and others who access the Service.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 2 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-blue-100 rounded-lg">
                                <ScrollText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">2. User Conduct & Academic Integrity</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    You agree to use FaheemGPT strictly for lawful educational purposes. To maintain the integrity of our platform, you must not:
                                </p>
                                <ul className="list-disc list-inside text-slate-600 space-y-2 pl-2">
                                    <li>Use the platform to cheat, facilitate plagiarism, or violate academic integrity policies of your institution.</li>
                                    <li>Attempt to reverse-engineer, decompile, or extract the AI models or platform infrastructure.</li>
                                    <li>Upload, share, or generate content that is offensive, harmful, infringing, or violates the rights of others.</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Section 3 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-purple-100 rounded-lg">
                                <AlertCircle className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">3. AI Limitations & Liability</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    FaheemGPT utilizes advanced artificial intelligence to provide tutoring assistance. While we strive for high accuracy:
                                </p>
                                <Card className="bg-amber-50 border-amber-100 shadow-none">
                                    <CardContent className="p-4 text-sm text-amber-800">
                                        <strong>Disclaimer:</strong> AI may occasionally produce incorrect or misleading information ("hallucinations"). Users should critically evaluate the AI's output and verify critical information with human instructors or official documentation. FaheemGPT is an educational aid, not a replacement for professional instruction.
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>

                    {/* Section 4 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-orange-100 rounded-lg">
                                <Copyright className="w-5 h-5 text-orange-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">4. Intellectual Property</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    All content, trademarks, logos, and code on FaheemGPT are the exclusive property of FaheemGPT Inc. or its licensors. The "FaheemGPT" curriculum and related educational materials are protected by copyright laws. You retain full ownership of your own personal learning data and progress logs.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 5 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-red-100 rounded-lg">
                                <Ban className="w-5 h-5 text-red-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">5. Termination</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    We reserve the right to suspend or terminate your account immediately, without prior notice or liability, if you breach these Terms, specifically including violations of our academic integrity or user conduct policies.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
                
                {/* Footer of Card */}
                <div className="p-8 bg-slate-50 border-t border-slate-200 text-center">
                    <p className="text-slate-600 mb-4">Have questions about our terms?</p>
                    <a href="mailto:legal@faheemgpt.com">
                        <Button variant="outline" className="bg-white">Contact Legal Team</Button>
                    </a>
                </div>
            </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
             <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900 font-medium text-slate-900">Terms</Link>
            <Link to="/support" className="hover:text-slate-900">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
